enum PublicationEnum
{
    Draft = 'Draft',
    Finished = 'Finished',
    Rejected = 'Rejected',
    Accepted = 'Accepted'
}

const PublicationOptions = (): Option[] => ([
    { value: PublicationEnum.Draft, text: $t('[[[Szkic]]]'), icon: 'fas fa-pen-circle text-info' },
    { value: PublicationEnum.Finished, text: $t('[[[Do akceptacji]]]'), icon: 'fas fa-clock text-warning' },
    { value: PublicationEnum.Rejected, text: $t('[[[Zwrócony]]]'), icon: 'fas fa-times-circle text-danger' },
    { value: PublicationEnum.Accepted, text: $t('[[[Zaakceptowany]]]'), icon: 'fas fa-check-circle text-success' }
]);

const AllPublicationOptions = (): Option[] => (
    [{ value: null, text: $t('[[[Wszystkie]]]') }].concat(PublicationOptions())
);

const PublicationName = (value: PublicationEnum): string => PublicationOptions().find(p => p.value === value)?.text || PublicationEnum[value];
const PublicationIcon = (value: PublicationEnum): string => PublicationOptions().find(p => p.value === value)?.icon || '';
const PublicationStyle = (classTemplate: string, status: PublicationEnum): Record<string, boolean> =>
{
    const classes: Record<string, boolean> = {};
    const re = new RegExp('\\{0\\}', 'g');

    classes[classTemplate.replace(re, 'info')] = status === PublicationEnum.Draft;
    classes[classTemplate.replace(re, 'warning')] = status === PublicationEnum.Finished;
    classes[classTemplate.replace(re, 'danger')] = status === PublicationEnum.Rejected;
    classes[classTemplate.replace(re, 'success')] = status === PublicationEnum.Accepted;

    return classes;
};

export {
    PublicationEnum,
    PublicationOptions,
    PublicationName,
    PublicationStyle,
    PublicationIcon,
    AllPublicationOptions
};

// ----------------------------------------------------------------------------------------------------

enum EventTypeEnum
{
    None = 'None',
    Course = 'Course',
    ExternalLink = 'ExternalLink'
}

const EventTypeOptions = (): Option[] => ([
    { value: EventTypeEnum.None, text: $t('[[[Brak]]]') },
    { value: EventTypeEnum.Course, text: $t('[[[Szkolenie]]]') },
    { value: EventTypeEnum.ExternalLink, text: $t('[[[Link]]]') }
]);

export {
    EventTypeEnum,
    EventTypeOptions
};

// ----------------------------------------------------------------------------------------------------

enum MaterialType
{
    Content = 'Content',
    Video = 'Video',
    SingleSelect = 'SingleSelect',
    MultiSelect = 'MultiSelect',
    TrueFalse = 'TrueFalse',
    SelectList = 'SelectList',
    FillTheGaps = 'FillTheGaps',
    CategoriesMatching = 'CategoriesMatching',
    ImagesMatching = 'ImagesMatching',
    MatchingPairs = 'MatchingPairs',
    SetInOrder = 'SetInOrder'
}

const MaterialTypeOptions = (): Option[] => ([
    { value: MaterialType.Content, text: $t('[[[Materiał opisowy]]]') },
    { value: MaterialType.Video, text: $t('[[[Materiał wideo]]]') },
    { value: MaterialType.SingleSelect, text: $t('[[[Jednokrotnego wyboru]]]') },
    { value: MaterialType.MultiSelect, text: $t('[[[Wielokrotnego wyboru]]]') },
    { value: MaterialType.TrueFalse, text: $t('[[[Prawda-Fałsz]]]') },
    { value: MaterialType.SelectList, text: $t('[[[Lista wyboru]]]') },
    { value: MaterialType.FillTheGaps, text: $t('[[[Uzupełnij luki]]]') },
    { value: MaterialType.CategoriesMatching, text: $t('[[[Dopasuj do kategorii]]]') },
    { value: MaterialType.ImagesMatching, text: $t('[[[Dopasuj do obrazka]]]') },
    { value: MaterialType.MatchingPairs, text: $t('[[[Połącz w pary]]]') },
    { value: MaterialType.SetInOrder, text: $t('[[[Ustaw w kolejności]]]') }
]);

enum TopicModeEnum
{
    Page = 'Page',
    Alias = 'Alias',
    Link = 'Link'
}

const TopicModeOptions = (): Option[] => ([
    { value: PageModeEnum.Page, text: $t('[[[Element grupujący]]]') },
    { value: PageModeEnum.Alias, text: $t('[[[Link do wybranej strony serwisu]]]') },
    { value: PageModeEnum.Link, text: $t('[[[Link do zewnętrznej strony]]]') }
]);

export {
    MaterialType,
    MaterialTypeOptions,
    TopicModeEnum,
    TopicModeOptions
};

// ----------------------------------------------------------------------------------------------------

enum CyclicPattern
{
    Daily = 1,
    Weekly = 2,
    Monthly = 3,
    RelativeMonthly = 4,
    Yearly = 5,
    RelativeYearly = 6
}

const DaysList = (): Option<number>[] => ([
    { value: 1, text: $t('[[[Poniedziałek]]]') },
    { value: 2, text: $t('[[[Wtorek]]]') },
    { value: 3, text: $t('[[[Środa]]]') },
    { value: 4, text: $t('[[[Czwartek]]]') },
    { value: 5, text: $t('[[[Piątek]]]') },
    { value: 6, text: $t('[[[Sobota]]]') },
    { value: 0, text: $t('[[[Niedziela]]]') }
]);

enum InvitationStatus
{
    ToAcceptance = 'ToAcceptance',
    Accepted = 'Accepted',
    Rejected = 'Rejected',
    Deleted = 'Deleted'
}

const InvitationStatusVariants = (): KeyValue[] => ([
    { key: InvitationStatus.ToAcceptance, value: 'primary' },
    { key: InvitationStatus.Accepted, value: 'success' },
    { key: InvitationStatus.Rejected, value: 'danger' }
]);

const InvitationStatuses = (): KeyValue[] => ([
    { key: InvitationStatus.ToAcceptance, value: $t('[[[Do akceptacji]]]') },
    { key: InvitationStatus.Accepted, value: $t('[[[Zaakceptowane]]]') },
    { key: InvitationStatus.Rejected, value: $t('[[[Odrzucone]]]') },
    { key: InvitationStatus.Deleted, value: $t('[[[Usunięte]]]') }
]);

export {
    CyclicPattern,
    DaysList,
    InvitationStatus,
    InvitationStatusVariants,
    InvitationStatuses
};

// ----------------------------------------------------------------------------------------------------------

enum EventCategoryColorEnum
{
    Primary = 'Primary',
    Dark = 'Dark',
    Muted = 'Muted',
    LightBlue = 'LightBlue',
    Yellow = 'Yellow',
    Red = 'Red',
    Green = 'Green',
    Gray = 'Gray',
    Blue = 'Blue'
}

const EventCategoryColorOptions = (): ColorOption[] => ([
    { value: EventCategoryColorEnum.Primary, text: $t('[[[Podstawowy]]]'), colorValue: '#0275d8' },
    { value: EventCategoryColorEnum.Dark, text: $t('[[[Czarny]]]'), colorValue: 'Black' },
    { value: EventCategoryColorEnum.Muted, text: $t('[[[Biały]]]'), colorValue: '#ababab' },
    { value: EventCategoryColorEnum.LightBlue, text: $t('[[[Jasny niebieski]]]'), colorValue: '#63C2DE' },
    { value: EventCategoryColorEnum.Yellow, text: $t('[[[Żółty]]]'), colorValue: '#FFC107' },
    { value: EventCategoryColorEnum.Red, text: $t('[[[Czerwony]]]'), colorValue: '#F86C6B' },
    { value: EventCategoryColorEnum.Green, text: $t('[[[Zielony]]]'), colorValue: '#4DBD74' },
    { value: EventCategoryColorEnum.Gray, text: $t('[[[Szary]]]'), colorValue: '#77797a' },
    { value: EventCategoryColorEnum.Blue, text: $t('[[[Niebieski]]]'), colorValue: '#00A2E8' }
]);

const EventCategoryColorOptionName = (value: EventCategoryColorEnum): string => EventCategoryColorOptions().find(p => p.value === value)?.text || EventCategoryColorEnum[value];

export {
    EventCategoryColorEnum,
    EventCategoryColorOptions,
    EventCategoryColorOptionName
};

// ----------------------------------------------------------------------------------------------------------

const MonthsList = (): Option<number>[] => ([
    { value: 0, text: $t('[[[styczniu]]]') },
    { value: 1, text: $t('[[[lutym]]]') },
    { value: 2, text: $t('[[[marcu]]]') },
    { value: 3, text: $t('[[[kwietniu]]]') },
    { value: 4, text: $t('[[[maju]]]') },
    { value: 5, text: $t('[[[czerwcu]]]') },
    { value: 6, text: $t('[[[lipcu]]]') },
    { value: 7, text: $t('[[[sierpniu]]]') },
    { value: 8, text: $t('[[[wrześniu]]]') },
    { value: 9, text: $t('[[[październiku]]]') },
    { value: 10, text: $t('[[[listopadzie]]]') },
    { value: 11, text: $t('[[[grudniu]]]') }
]);

const MonthOptionName = (value: number): string => MonthsList().find(p => p.value === value)?.text;

export {
    MonthsList,
    MonthOptionName
};

export enum ModulesKey
{
    LESSONS = 'el_lessons',
    TESTS = 'el_tests',
    VIDEO = 'el_video_lessons',
    SCORM = 'el_scorm_lessons',
    TOPICS = 'el_topics',
    LINKELEMENT = 'el_link_element'
}

const ModulesNames = (): KeyValue[] => ([
    { key: 'el_lessons', value: $t('[[[Lekcja]]]') },
    { key: 'el_tests', value: $t('[[[Test]]]') },
    { key: 'el_video_lessons', value: $t('[[[Video]]]') },
    { key: 'el_scorm_lessons', value: $t('[[[Scorm]]]') },
    { key: 'el_topics', value: $t('[[[Temat]]]') },
    { key: 'el_link_element', value: $t('[[[Link]]]') }
]);

export const ModuleOptionName = (key: string): string => ModulesNames().find(p => p.key === key)?.value;

enum Locale
{
    EN = 'en',
    PL = 'pl'
}

const LocaleOptions = (): Option[] => ([
    { value: Locale.EN, text: $t('[[[Angielski]]]') },
    { value: Locale.PL, text: $t('[[[Polski]]]') }
]);

export const LocaleOptionText = (key: Locale | string): string => LocaleOptions().find(p => p.value === key)?.text;

// ----------------------------------------------------------------------------------------------------------

enum UserGenderEnum
{
    NoSpecified = 'NoSpecified',
    Male = 'Male',
    Female = 'Female'
}

const UserGenderEnumOptions = (): Option[] => ([
    { value: UserGenderEnum.Male, text: $t('[[[Mężczyzna]]]') },
    { value: UserGenderEnum.Female, text: $t('[[[Kobieta]]]') },
    { value: UserGenderEnum.NoSpecified, text: $t('[[[Nie podano]]]') }
]);

const UserGenderEnumOptionName = (value: UserGenderEnum): string => UserGenderEnumOptions().find(p => p.value === value)?.text || UserGenderEnum[value];

export {
    UserGenderEnum,
    UserGenderEnumOptions,
    UserGenderEnumOptionName
};

// ----------------------------------------------------------------------------------------------------------

enum StatusCourseEnum
{
    NotStarted = 'NotStarted',
    InProgress = 'InProgress',
    Passed = 'Passed',
    NotPassed = 'NotPassed'
}

const StatusCourseEnumOptions = (): Option[] => ([
    { value: StatusCourseEnum.NotStarted, text: $t('[[[Nierozpoczęty]]]') },
    { value: StatusCourseEnum.InProgress, text: $t('[[[W trakcie]]]') },
    { value: StatusCourseEnum.Passed, text: $t('[[[Zaliczony]]]') },
    { value: StatusCourseEnum.NotPassed, text: $t('[[[Niezaliczony]]]') }
]);

const StatusCourseEnumOptionName = (value: StatusCourseEnum): string => StatusCourseEnumOptions().find(p => p.value === value)?.text || StatusCourseEnum[value];

export {
    StatusCourseEnum,
    StatusCourseEnumOptions,
    StatusCourseEnumOptionName
};

// ----------------------------------------------------------------------------------------------------

enum AutomationEvents
{
    DocumentChanged = 'DocumentChanged'
}

export {
    AutomationEvents
};

// ----------------------------------------------------------------------------------------------------------

enum MeetingTypeEnum
{
    None = 'None',
    Course = 'Course',
    Event = 'Event',
    Webinar = 'Webinar'
}

const MeetingTypeEnumOptions = (): Option[] => ([
    { value: MeetingTypeEnum.None, text: $t('[[[Brak]]]') },
    { value: MeetingTypeEnum.Course, text: $t('[[[Szkolenie]]]') },
    { value: MeetingTypeEnum.Event, text: $t('[[[Wydarzenie z kalendarza]]]') },
    { value: MeetingTypeEnum.Webinar, text: $t('[[[Webinar]]]') }
]);

const MeetingTypeEnumOptionName = (value: MeetingTypeEnum): string => MeetingTypeEnumOptions().find(p => p.value === value)?.text || MeetingTypeEnum[value];

export {
    MeetingTypeEnum,
    MeetingTypeEnumOptions,
    MeetingTypeEnumOptionName
};

// ----------------------------------------------------------------------------------------------------------

enum ApplicationStatusEnum
{
    Pending = 'Pending',
    Review = 'Review',
    Qualified = 'Qualified',
    ReserveList = 'ReserveList',
    Rejected = 'Rejected',
    Cancellation = 'Cancellation'
}

const ApplicationStatusOptions = (): ColorOption[] => ([
    { value: ApplicationStatusEnum.Pending, text: $t('[[[Oczekuje na akceptację]]]'), colorValue: '#FFA500' },
    { value: ApplicationStatusEnum.Review, text: $t('[[[W trakcie weryfikacji]]]'), colorValue: 'Black' },
    { value: ApplicationStatusEnum.Qualified, text: $t('[[[Zaakceptowane]]]'), colorValue: '#4DBD74' },
    { value: ApplicationStatusEnum.ReserveList, text: $t('[[[Lista rezerwowa]]]'), colorValue: '#00A2E8' },
    { value: ApplicationStatusEnum.Rejected, text: $t('[[[Odrzucone]]]'), colorValue: '#F86C6B' },
    { value: ApplicationStatusEnum.Cancellation, text: $t('[[[Rezygnacja]]]'), colorValue: '#C8CED3' }
]);

const ApplicationStatusOptionName = (value: ApplicationStatusEnum): string => ApplicationStatusOptions().find(p => p.value === value)?.text || ApplicationStatusEnum[value];
const ApplicationStatusOptionColor = (value: ApplicationStatusEnum): string => ApplicationStatusOptions().find(p => p.value === value)?.colorValue || '';

export {
    ApplicationStatusEnum,
    ApplicationStatusOptions,
    ApplicationStatusOptionName,
    ApplicationStatusOptionColor
};

// ----------------------------------------------------------------------------------------------------------

enum UserAccessInfoTypeEnum
{
    Success = 'Success',
    Info = 'Info',
    Danger = 'Danger'
}

const UserAccessInfoTypeEnumOptions = (): ColorOption[] => ([
    { value: UserAccessInfoTypeEnum.Success, text: $t('[[[Aktualny]]]'), colorValue: 'var(--bs-success)' },
    { value: UserAccessInfoTypeEnum.Info, text: $t('[[[Zakończony]]]'), colorValue: 'var(--bs-info)' },
    { value: UserAccessInfoTypeEnum.Danger, text: $t('[[[Użytkownik usunięty z zespołu]]]'), colorValue: 'var(--bs-danger)' }
]);

const UserAccessInfoTypeEnumOptionName = (value: UserAccessInfoTypeEnum): string => UserAccessInfoTypeEnumOptions().find(p => p.value === value)?.text || ApplicationCourseTypeEnum[value];
const UserAccessInfoOptionColor = (value: UserAccessInfoTypeEnum): string => UserAccessInfoTypeEnumOptions().find(p => p.value === value)?.colorValue || '';

export {
    UserAccessInfoTypeEnum,
    UserAccessInfoTypeEnumOptions,
    UserAccessInfoTypeEnumOptionName,
    UserAccessInfoOptionColor
};

enum EnvironmentTypeEnum
{
    Test = 'Test',
    Production = 'Production'
}

const EnvironmentTypeOptions = (): Option[] => ([
    { value: EnvironmentTypeEnum.Test, text: $t('[[[Testowe]]]') },
    { value: EnvironmentTypeEnum.Production, text: $t('[[[Produkcyjne]]]') }
]);

const EnvironmentTypeName = (value: EnvironmentTypeEnum): string => EnvironmentTypeOptions().find(p => p.value === value)?.text || EnvironmentTypeEnum[value];

export {
    EnvironmentTypeEnum,
    EnvironmentTypeOptions,
    EnvironmentTypeName
};

enum BorderStyleEnum
{
    None = 'none',
    Dashed = 'dashed',
    Dotted = 'dotted',
    Solid = 'solid'
}

const BorderStyleOptions = (): Option[] => ([
    { value: BorderStyleEnum.None, text: $t('[[[Brak]]]') },
    { value: BorderStyleEnum.Dashed, text: $t('[[[Serie kresek lub fragmentów linii]]]') },
    { value: BorderStyleEnum.Dotted, text: $t('[[[Serie kropek]]]') },
    { value: BorderStyleEnum.Solid, text: $t('[[[Pojedyńcza, prosta, ciągła linia]]]') }
]);

export {
    BorderStyleEnum,
    BorderStyleOptions
};

// ----------------------------------------------------------------------------------------------------

enum TextDecorationStyleEnum
{
    Solid = 'solid',
    Double = 'double',
    Dotted = 'dotted',
    Dashed = 'dashed',
    Wavy = 'wavy'
}

const TextDecorationStyleOptions = (): Option[] => ([
    { value: TextDecorationStyleEnum.Solid, text: $t('[[[Linia ciągła]]]') },
    { value: TextDecorationStyleEnum.Double, text: $t('[[[Podwójna linia ciągła]]]') },
    { value: TextDecorationStyleEnum.Dotted, text: $t('[[[Linia kropkowana]]]') },
    { value: TextDecorationStyleEnum.Dashed, text: $t('[[[Linia kreskowana]]]') },
    { value: TextDecorationStyleEnum.Wavy, text: $t('[[[Linia falująca]]]') }
]);

export {
    TextDecorationStyleEnum,
    TextDecorationStyleOptions
};
